import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import Select from 'react-select';
import Swal from "sweetalert2";
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const CourtCreationNew = () => {
  const { selectedCenter } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const courtData = queryParams.get('courtId');
  const isEditMode = !!courtData;
  const token = localStorage.getItem('auth-token');

  const [courtNames, setCourtNames] = useState(['']);
  const [sport, setSport] = useState('');
  const [maxPlayers, setMaxPlayers] = useState('');
  const [courtType, setCourtType] = useState('');
  const [slotDuration, setSlotDuration] = useState(30);
  const [timings, setTimings] = useState([{ openTime: '', closeTime: '' }]);
  const [sportsList, setSportsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);
  const [editLoaded, setEditLoaded] = useState(false);

  const generateTimeOptions = (duration = 15) => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += duration) {
        const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        const label = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
        times.push({ value: time24, label });
      }
    }
    return times;
  };

  const [timeOptions, setTimeOptions] = useState(generateTimeOptions(slotDuration));
  useEffect(() => {
    setTimeOptions(generateTimeOptions(slotDuration));
    if (!isEditMode) {
      setTimings([{ openTime: '', closeTime: '' }]);
    }
  }, [slotDuration, isEditMode]);
  

  useEffect(() => {
    if (!selectedCenter) return;
    const fetchSports = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_SPORT_COURT_TYPE}?academyIds=${selectedCenter}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const payload = await res.json();
        if (res.ok && payload.success) {
          const sports = (payload.data_list || []).map(s => ({
            id: s.sportId,
            name: s.sportName,
            courtTypeOptions: Object.entries(s.CourtTypes || {}).map(
              ([typeId, typeName]) => ({ value: Number(typeId), label: typeName })
            )
          }));
          setSportsList(sports);
        } else {
          throw new Error(payload.success_message || 'Error fetching sports');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSports();
  }, [selectedCenter]);

  const courtTypeOptions = sport
    ? (sportsList.find(s => s.id === Number(sport)) || { courtTypeOptions: [] }).courtTypeOptions
    : [];

  useEffect(() => {
    if (!courtData) return;
    const fetchCourtDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_EDIT_COURT}/${courtData}/edit-new`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          }
        });
        const data = await response.json();
        if (response.ok && data.success) {
          const court = data.data.courtForm;
          setCourtNames([court.name]);
          setSport(court.sportId?.toString() || '');
          setMaxPlayers(court.maxPlayersAllowed?.toString() || '');
          setCourtType(court.courtTypeId?.toString() || '');
          setTimings(
            (court.courtTimings || []).map(t => ({
              openTime: t.open_time,  
              closeTime: t.close_time
            }))          
          );
          setSlotDuration(court.slotDurationMinutes || 30);
          setEditLoaded(true);
        } else {
          throw new Error(data.success_message || 'Error fetching court details');
        }
      } catch (err) {
        setError(err.message);
      }
    };
    fetchCourtDetails();
  }, [courtData]);

  const addTiming = () => setTimings([...timings, { openTime: '', closeTime: '' }]);
  const removeTiming = (index) => {
    if (timings.length > 1) setTimings(timings.filter((_, i) => i !== index));
  };
  const updateTiming = (index, field, value) => {
    const updatedTimings = [...timings];
    updatedTimings[index][field] = value;
    setTimings(updatedTimings);
  };

  const addCourtName = () => setCourtNames([...courtNames, '']);
  const updateCourtName = (i, val) => {
    const arr = [...courtNames];
    arr[i] = val;
    setCourtNames(arr);
  };
  const removeCourtName = (i) => {
    if (courtNames.length > 1) setCourtNames(courtNames.filter((_, idx) => idx !== i));
  };
  const normalizeTime = (time) => {
    if (!time) return time;
    if (/^\d{2}:\d{2}:\d{2}:00$/.test(time)) return time.slice(0, -3);
    if (/^\d{2}:\d{2}$/.test(time)) return time + ":00";
    return time; 
  };
  
  const buildSubmitPayload = () => {
    const newFlowStartTimes = timings
    .filter(({ openTime }) => openTime)
    .map(({ openTime }) => normalizeTime(openTime));
  
  const newFlowEndTimes = timings
    .filter(({ closeTime }) => closeTime)
    .map(({ closeTime }) => normalizeTime(closeTime));
  

    const basePayload = {
      academyId: selectedCenter || null,
      sportId: Number(sport) || null,
      courtTypeId: Number(courtType) || null,
      maxPlayersAllowed: maxPlayers ? parseInt(maxPlayers, 10) : null,
      newFlowStartTimes,
      newFlowEndTimes,
      slotDurationMinutes: slotDuration,
    };

    if (isEditMode) {
      return {
        ...basePayload,
        ids: [Number(courtData)],
        courtNames: courtNames.filter(n => n.trim() !== ''),
      };
    } else {
      return {
        ...basePayload,
        names: courtNames.filter(n => n.trim() !== ''),
      };
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courtNames.length || courtNames.some(n => !n.trim()) || !sport || !maxPlayers || !timings.length || !courtType) {
      Swal.fire({ icon: "warning", title: "Incomplete Form", text: "Fill all required fields.", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      return;
    }
    const newPayload = buildSubmitPayload();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(process.env.REACT_APP_API_CREATE_COURT_NEW, {
        method: "POST",
        headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newPayload),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        navigate("/courtlist");
        setFormSubmitted(true);
        Swal.fire({ icon: "success", title: "Success", text: "Court submitted successfully!", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      } else throw new Error(data.success_message || "Error submitting court data");
    } catch (err) {
      setError(err.message);
      Swal.fire({ icon: "error", title: "Submission Failed", text: err.message, toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container" style={{ overflow: 'visible' }}>
      <div className="breadcrumb">
        <Link to="/courtlist" className="breadcrumb-link">Court List</Link> &gt; {isEditMode ? 'Edit Court' : 'New Court'}
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          {courtNames.map((courtName, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "20px"
              }}
            >
              <div style={{ flex: 1, position: "relative" }}>
                <input
                  type="text"
                  value={courtName}
                  onChange={(e) => updateCourtName(i, e.target.value)}
                  placeholder="Enter Court Name"
                  required
                  className={`walkin-form has-value`}
                />
                <label
                  htmlFor="courtName"
                  style={{
                    position: "absolute",
                    top: "-8px",
                    left: "10px",
                    fontSize: "12px",
                    color: "#555",
                    backgroundColor: "white",
                    padding: "0 5px"
                  }}
                >
                  Court Name
                </label>
              </div>

              {courtNames.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeCourtName(i)}
                  className="new-court-remove-button"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
        {!isEditMode && (
          <div style={{ marginTop: '-18px' }}>
            <button type="button" onClick={addCourtName} className="plusButton">
              <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
              </svg>
            </button>
          </div>
        )}

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            name="maxPlayers"
            className={`walkin-form ${maxPlayers ? "has-value" : ""}`}
            value={maxPlayers}
            onChange={(e) => setMaxPlayers(e.target.value)}
            placeholder="Enter Max Players"
            required
            min="1"
            onWheel={e => e.target.blur()}
            onKeyDown={e => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          />
          {maxPlayers && <label htmlFor="maxPlayers">Max Players per Slot</label>}
        </div>

        <div className="new-court-form-group" style={{ display: 'flex', marginTop: '10px' }}>
          <label className="new-court-label">Sport:</label>
          <div className="sport-options">
            {sportsList.map((sportItem) => (
              <label key={sportItem.id} className="radio-label">
                <input
                  type="radio"
                  name="sport"
                  value={sportItem.id}
                  checked={sport === sportItem.id.toString()}
                  onChange={(e) => {
                    setSport(e.target.value);
                    setCourtType('');
                  }}
                  required
                />
                {sportItem.name}
              </label>
            ))}
          </div>
        </div>

        <div className="floating-select" style={{ width: '98%' }}>
          <Select
            options={courtTypeOptions}
            value={courtTypeOptions.find(option => option.value === Number(courtType))}
            onChange={(selectedOption) => setCourtType(selectedOption.value)}
            placeholder="Select Court Type"
            className={`walkin-form-dropdown ${courtType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isSearchable
            styles={{
              control: (provided) => ({ ...provided, padding: '3px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {courtType && (
            <label style={{ position: 'absolute', top: '-8px', left: '10px', fontSize: '12px', color: '#555', backgroundColor: 'white', padding: '0 5px' }}>
              Court Type
            </label>
          )}
        </div>
        {!isEditMode && (
          <div className="new-court-form-group" style={{ display: 'flex', marginTop: '20px' }}>
            <label className="new-court-label">Slot Duration:</label>
            <div className="sport-options">
              {[30, 60].map(d => (
                <label key={d} className="radio-label">
                  <input
                    type="radio"
                    name="slotDuration"
                    value={d}
                    checked={slotDuration === d}
                    onChange={(e) => setSlotDuration(Number(e.target.value))}
                    required
                  />
                  {d} Minutes
                </label>
              ))}
            </div>
          </div>
       )}

        <h3>Timings</h3>
        <table className="new-court-table">
          <thead>
            <tr>
              <th>Open Time</th>
              <th>Close Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {timings.map((timing, index) => (
              <tr key={`${index}-${slotDuration}`}>
                <td>
                  <Select
                    key={`open-${index}-${slotDuration}`}
                    value={timeOptions.find(option => option.value === timing.openTime) || null}
                    onChange={(selectedOption) => updateTiming(index, 'openTime', selectedOption.value)}
                    options={timeOptions}
                    className="new-court-time-select"
                    isSearchable
                    menuPortalTarget={document.body}
                  />
                </td>
                <td>
                  <Select
                    key={`close-${index}-${slotDuration}`}
                    value={timeOptions.find(option => option.value === timing.closeTime) || null}
                    onChange={(selectedOption) => updateTiming(index, 'closeTime', selectedOption.value)}
                    options={timeOptions}
                    className="new-court-time-select"
                    isSearchable
                    menuPortalTarget={document.body}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() => removeTiming(index)}
                    className="new-court-remove-button"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>

        </table>

        <div>
          <button
            type="button"
            className="plusButton"
            onClick={addTiming}
            title="Add Plan Detail"
          >
            <svg className="plusIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
              <g>
                <path d="M13.75 23.75V16.25H6.25V13.75H13.75V6.25H16.25V13.75H23.75V16.25H16.25V23.75H13.75Z"></path>
              </g>
            </svg>
          </button>
        </div>

        <div className="new-court-submit-container">
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourtCreationNew